import React, { forwardRef, useImperativeHandle, useState } from 'react';
import s from './s.module.less';
import { INSURANCE_TYPE_INSURANCE, INSURANCE_TYPE_CASH, INSURANCE_TYPE_CASH_AND_INSURANCE } from 'constants/common';
import { EPPAStep, IPPAServiceDetailResponse } from 'types/ppaOnboarding';
import ManagedBillingModal from './ManagedBillingModal';

interface PaymentMethod {
    value: string;
    label: string;
}

interface IProps {
    onNext: (data: { supportPayType: string }, step: EPPAStep) => void;
    serviceDetail?: IPPAServiceDetailResponse;
    onSelectionChange?: (val: string) => void;
}

export interface IStep3Ref {
    submit: () => void;
}

const MANAGED_BILLING_DISMISSED_KEY = 'managed_billing_modal_dismissed';

const Step3 = forwardRef<IStep3Ref, IProps>(({ onNext, serviceDetail, onSelectionChange }, ref) => {
    const [selectedPayment, setSelectedPayment] = useState<string>(serviceDetail?.supportPayType || '');
    const [showModal, setShowModal] = useState<boolean>(false);
    const [pendingPayment, setPendingPayment] = useState<string>('');

    const paymentMethods: PaymentMethod[] = [
        {
            value: INSURANCE_TYPE_INSURANCE,
            label: 'Accept insurance only',
        },
        {
            value: INSURANCE_TYPE_CASH,
            label: 'Cash pay only',
        },
        {
            value: INSURANCE_TYPE_CASH_AND_INSURANCE,
            label: 'Both insurance and cash',
        },
    ];

    const proceedToNext = (value: string) => {
        onNext?.({ supportPayType: value }, EPPAStep.STEP_7);
    };

    const handlePaymentSelect = (value: string) => {
        setSelectedPayment(value);
        onSelectionChange?.(value);

        const isInsuranceRelated = value === INSURANCE_TYPE_INSURANCE || value === INSURANCE_TYPE_CASH_AND_INSURANCE;
        const alreadyDismissed = localStorage.getItem(MANAGED_BILLING_DISMISSED_KEY) === 'true';

        if (isInsuranceRelated && !alreadyDismissed) {
            setPendingPayment(value);
            setShowModal(true);
        } else {
            proceedToNext(value);
        }
    };

    // Allow the footer "Next" button to behave exactly like clicking the currently selected option.
    useImperativeHandle(
        ref,
        () => ({
            submit: () => {
                if (selectedPayment) {
                    handlePaymentSelect(selectedPayment);
                }
            },
        }),
        [selectedPayment],
    );

    const setDismissed = () => {
        // TODO: replace localStorage with backend persistence (user.has_dismissed_managed_billing_modal)
        // Backend schema pending (tracked with eng team)
        localStorage.setItem(MANAGED_BILLING_DISMISSED_KEY, 'true');
    };

    const handleDismiss = () => {
        setDismissed();
        setShowModal(false);
        proceedToNext(pendingPayment);
    };

    const handleInterested = () => {
        setDismissed();
        setShowModal(false);
        proceedToNext(pendingPayment);
    };

    return (
        <div className={s.wrap}>
            <div className={s.header}>
                <h1>How would you like to get paid?</h1>
                <p>Please select your preferred payment method.</p>
            </div>

            <div className={s.optionsContainer}>
                {paymentMethods.map((method) => (
                    <div
                        key={method.value}
                        className={`${s.option} ${selectedPayment === method.value ? s.selected : ''}`}
                        onClick={() => handlePaymentSelect(method.value)}
                    >
                        {method.label}
                    </div>
                ))}
            </div>

            <ManagedBillingModal open={showModal} onDismiss={handleDismiss} onInterested={handleInterested} />
        </div>
    );
});

Step3.displayName = 'SetupServiceStep3';

export default Step3;
